/**
 * LeaveApprovalsWidget — Owner dashboard, pending leave requests with
 * inline Approve/Deny + note (Prompt 11E.8). Invokes the existing 11E.6
 * approve/deny endpoints; the query invalidation in useApproveLeave/
 * useDenyLeave already drops an approved/denied row out of the queue.
 *
 * data-testid: candeza-dash-leave-approvals
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, Button, Badge } from '@/components/common';
import { useLeaveQueue, useApproveLeave, useDenyLeave, type LeaveResponse } from '@features/attendance/api/leaves';

const Row = styled.div`
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child {
    border-bottom: none;
  }
`;

const RowTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StaffLine = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const MetaLine = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const NoteInput = styled.input`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  font-size: 13px;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const EmptyNote = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  margin: 0;
`;

const LeaveRow: React.FC<{ leave: LeaveResponse }> = ({ leave }) => {
  const [note, setNote] = useState('');
  const approve = useApproveLeave();
  const deny = useDenyLeave();
  const busy = approve.isPending || deny.isPending;

  const decide = async (action: 'approve' | 'deny') => {
    const mut = action === 'approve' ? approve : deny;
    try {
      await mut.mutateAsync({ id: leave.id, body: { note: note.trim() || undefined } });
      toast.success(action === 'approve' ? 'Leave approved' : 'Leave denied');
    } catch {
      // client toasts the failure
    }
  };

  return (
    <Row data-testid={`candeza-dash-leave-approval-row-${leave.id}`}>
      <RowTop>
        <div>
          <StaffLine>{leave.staff_id}</StaffLine>
          <MetaLine>
            {leave.leave_type} · {leave.start_date} → {leave.end_date} ({leave.days} day{leave.days === 1 ? '' : 's'})
          </MetaLine>
        </div>
        <Actions>
          <Button
            variant="primary"
            size="sm"
            isLoading={approve.isPending}
            disabled={busy}
            onClick={() => decide('approve')}
            data-testid={`candeza-dash-action-approve-leave-${leave.id}`}
          >
            Approve
          </Button>
          <Button
            variant="ghost"
            size="sm"
            isLoading={deny.isPending}
            disabled={busy}
            onClick={() => decide('deny')}
            data-testid={`candeza-dash-action-deny-leave-${leave.id}`}
          >
            Deny
          </Button>
        </Actions>
      </RowTop>
      <NoteInput
        placeholder="Optional note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid={`candeza-dash-leave-approval-note-${leave.id}`}
      />
    </Row>
  );
};

export const LeaveApprovalsWidget: React.FC = () => {
  const { data, isLoading } = useLeaveQueue('pending');
  const items = data?.items ?? [];

  return (
    <Card padding="lg" data-testid="candeza-dash-leave-approvals">
      <CardHeader>
        <CardTitle>Leave Approvals</CardTitle>
        {!isLoading && <Badge variant={items.length > 0 ? 'warning' : 'success'}>{items.length}</Badge>}
      </CardHeader>
      {isLoading && <EmptyNote>Loading…</EmptyNote>}
      {!isLoading && items.length === 0 && <EmptyNote>No pending leave requests.</EmptyNote>}
      {items.map((leave) => (
        <LeaveRow key={leave.id} leave={leave} />
      ))}
    </Card>
  );
};
